import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  useSlotGroup,
  EditableSlotGroup,
  RichTextContent,
} from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';
import { resolveHref } from '../utils/resolveHref';
import { useCtaColorVars } from '../hooks/useCtaColorVars';

// ============================================================================
// Slot group definitions (inline literals — required for schema extraction)
// ============================================================================
//
// Three rewards programs are rendered as alternating image / copy rows
// (zig-zag). Each row owns:
//   - an image slot group (image + alt text) — defaults to a photo gallery
//     entry so the section always has visuals on a fresh property
//   - a CTA slot group (url + label + target)
// Richtext tokens for eyebrow, title, and body are declared further down.

// Unsplash stock photos (free, hotlink-safe via images.unsplash.com) so the
// Rewards Programs cards always render with real imagery on a fresh property,
// even before anyone uploads community photos. sourceBinding + generator are
// retained so the editor's media picker and AI-fill can still swap in a
// property-specific photo per community.
const program1ImageDef: SlotGroupDefinition = {
  label: 'Program 1 Image',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      // Friends sharing a meal at home — "refer a friend" vibe.
      defaultValue:
        'https://images.unsplash.com/photo-1529333166437-7750a6dd5a70?w=1200&q=80&auto=format&fit=crop',
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultValue: 'Friends gathering together at home',
    },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 1,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
  generator: {
    prompt:
      'Pick a lifestyle / community photo that evokes residents sharing their home — a social lounge, courtyard gathering area, clubroom, exterior entry, or any welcoming common space. Prefer warm, people-friendly scenes. Skip unit interiors with floor-plan diagrams or site maps.',
    context:
      'Illustrates the Resident Referral Program card on the Rewards Programs page.',
  },
};

const program1CtaDef: SlotGroupDefinition = {
  label: 'Program 1 CTA',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultValue: '/contact-us',
      suggestedFields: ['property_website_url'],
    },
    label: {
      label: 'Link Text',
      type: 'text',
      defaultValue: 'Refer a Friend',
    },
    _target: {
      label: 'Target',
      type: 'text',
      defaultValue: '_self',
    },
  },
};

const program2ImageDef: SlotGroupDefinition = {
  label: 'Program 2 Image',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      // Professional team at work — "employer discount" vibe.
      defaultValue:
        'https://images.unsplash.com/photo-1521737711867-e3b97375f902?w=1200&q=80&auto=format&fit=crop',
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultValue: 'Professionals collaborating at work',
    },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 3,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
  generator: {
    prompt:
      'Pick a photo that reads as professional / work-friendly — a coworking lounge, business center, clubroom with laptop-friendly seating, lobby, or any polished common space that signals a job-friendly community. Skip unit interiors with floor-plan diagrams or site maps.',
    context:
      'Illustrates the Preferred Employer Program card on the Rewards Programs page.',
  },
};

const program2CtaDef: SlotGroupDefinition = {
  label: 'Program 2 CTA',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultValue: '/contact-us',
      suggestedFields: ['property_website_url'],
    },
    label: {
      label: 'Link Text',
      type: 'text',
      defaultValue: 'Check Your Employer',
    },
    _target: {
      label: 'Target',
      type: 'text',
      defaultValue: '_self',
    },
  },
};

const program3ImageDef: SlotGroupDefinition = {
  label: 'Program 3 Image',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      // Keys being handed off — "ambassador welcoming a new resident" vibe.
      defaultValue:
        'https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=1200&q=80&auto=format&fit=crop',
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultValue: 'Welcoming a new resident home',
    },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 5,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
  generator: {
    prompt:
      'Pick a photo that reads as a resident ambassador scene — a tour-friendly space, leasing lounge, rooftop or amenity area where a resident might show off the property, or a vibrant outdoor community spot. Skip unit interiors with floor-plan diagrams or site maps.',
    context:
      'Illustrates the Community Ambassador Program card on the Rewards Programs page.',
  },
};

const program3CtaDef: SlotGroupDefinition = {
  label: 'Program 3 CTA',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultValue: '/contact-us',
      suggestedFields: ['property_website_url'],
    },
    label: {
      label: 'Link Text',
      type: 'text',
      defaultValue: 'Become an Ambassador',
    },
    _target: {
      label: 'Target',
      type: 'text',
      defaultValue: '_self',
    },
  },
};

// ============================================================================
// Reusable chevron arrow for every program CTA
// ============================================================================

function ArrowIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <line x1="5" y1="12" x2="19" y2="12" />
      <polyline points="12 5 19 12 12 19" />
    </svg>
  );
}

// ============================================================================
// Main component
// ============================================================================

export function RewardsPrograms() {
  useRevealStyles();
  const introRef = useScrollReveal<HTMLDivElement>();
  const row1Ref = useScrollReveal<HTMLDivElement>();
  const row2Ref = useScrollReveal<HTMLDivElement>();
  const row3Ref = useScrollReveal<HTMLDivElement>();

  const program1Image = useSlotGroup('rewardsprograms.program1_image', program1ImageDef);
  const program1Cta = useSlotGroup('rewardsprograms.program1_cta', program1CtaDef);
  const program2Image = useSlotGroup('rewardsprograms.program2_image', program2ImageDef);
  const program2Cta = useSlotGroup('rewardsprograms.program2_cta', program2CtaDef);
  const program3Image = useSlotGroup('rewardsprograms.program3_image', program3ImageDef);
  const program3Cta = useSlotGroup('rewardsprograms.program3_cta', program3CtaDef);
  const ctaColorVars = useCtaColorVars();

  return (
    <section className="rp-section" style={ctaColorVars}>
      <div className="rp-container">
        {/* Intro — centered subtitle + body sits above the three program rows. */}
        <div ref={introRef} className="reveal-fade-up rp-intro">
          <RichTextContent
            path="rewardsprograms.intro_eyebrow"
            label="Intro Eyebrow"
            defaultValue="<p>Live Here &amp; Get Rewarded</p>"
            as="p"
            className="rp-intro-eyebrow"
          />
          <RichTextContent
            path="rewardsprograms.intro_title"
            label="Intro Title"
            defaultValue="<p>Living here comes with real opportunities to earn.</p>"
            as="h2"
            className="rp-intro-title"
          />
          <RichTextContent
            path="rewardsprograms.intro_body"
            label="Intro Body"
            defaultValue='<p>At <span data-type="field-token" data-field="property_name"></span>, our rewards programs are built to offset rent, reduce move-in costs, and create a stronger, more connected community. Explore the three ways our residents earn below.</p>'
            as="div"
            className="rp-intro-body"
          />
        </div>

        {/* Three programs side-by-side on desktop, 2-up on tablet, stacked
            on mobile — same card grid rhythm as FlexibleLivingPrograms. */}
        <div className="rp-grid">
        {/* Program 1 — Resident Referral Program */}
        <article ref={row1Ref} className="reveal-fade-up rp-card">
          <EditableSlotGroup
            groupPath="rewardsprograms.program1_image"
            definition={program1ImageDef}
            className="rp-card-media"
          >
            <div className="rp-card-image-wrap">
              {program1Image.image ? (
                <VibesitesImage
                  url={program1Image.image}
                  alt={program1Image.alt || ''}
                  sizes="(min-width: 1024px) 33vw, (min-width: 640px) 50vw, 100vw"
                  className="rp-card-image"
                />
              ) : (
                <div className="rp-card-image rp-card-image--placeholder" />
              )}
            </div>
          </EditableSlotGroup>
          <div className="rp-card-body">
            <RichTextContent
              path="rewardsprograms.program1_eyebrow"
              label="Program 1 Eyebrow"
              defaultValue="<p>Resident Referral Program</p>"
              as="p"
              className="rp-card-eyebrow"
            />
            <RichTextContent
              path="rewardsprograms.program1_title"
              label="Program 1 Title"
              defaultValue="<p>Love Your Home? It Pays to Share It.</p>"
              as="h3"
              className="rp-card-title"
            />
            <RichTextContent
              path="rewardsprograms.program1_body"
              label="Program 1 Body"
              defaultValue="<p>Earn rent credits when you refer a friend who signs a lease. Share the home you love — and let your referrals help pay for yours.</p>"
              as="div"
              className="rp-card-text"
            />
            <EditableSlotGroup
              groupPath="rewardsprograms.program1_cta"
              definition={program1CtaDef}
            >
              <a
                href={resolveHref(program1Cta.url || '#')}
                target={program1Cta._target || '_self'}
                rel={program1Cta._target === '_blank' ? 'noopener noreferrer' : undefined}
                className="rp-card-cta"
              >
                <span>{program1Cta.label || 'Learn More'}</span>
                <ArrowIcon />
              </a>
            </EditableSlotGroup>
          </div>
        </article>

        {/* Program 2 — Preferred Employer Program */}
        <article ref={row2Ref} className="reveal-fade-up rp-card">
          <EditableSlotGroup
            groupPath="rewardsprograms.program2_image"
            definition={program2ImageDef}
            className="rp-card-media"
          >
            <div className="rp-card-image-wrap">
              {program2Image.image ? (
                <VibesitesImage
                  url={program2Image.image}
                  alt={program2Image.alt || ''}
                  sizes="(min-width: 1024px) 33vw, (min-width: 640px) 50vw, 100vw"
                  className="rp-card-image"
                />
              ) : (
                <div className="rp-card-image rp-card-image--placeholder" />
              )}
            </div>
          </EditableSlotGroup>
          <div className="rp-card-body">
            <RichTextContent
              path="rewardsprograms.program2_eyebrow"
              label="Program 2 Eyebrow"
              defaultValue="<p>Preferred Employer Program</p>"
              as="p"
              className="rp-card-eyebrow"
            />
            <RichTextContent
              path="rewardsprograms.program2_title"
              label="Program 2 Title"
              defaultValue="<p>Your Job Comes with Built-In Savings.</p>"
              as="h3"
              className="rp-card-title"
            />
            <RichTextContent
              path="rewardsprograms.program2_body"
              label="Program 2 Body"
              defaultValue="<p>Eligible employees from partner companies receive monthly rent reductions on top of any current leasing specials. Ask our team to see if your employer qualifies.</p>"
              as="div"
              className="rp-card-text"
            />
            <EditableSlotGroup
              groupPath="rewardsprograms.program2_cta"
              definition={program2CtaDef}
            >
              <a
                href={resolveHref(program2Cta.url || '#')}
                target={program2Cta._target || '_self'}
                rel={program2Cta._target === '_blank' ? 'noopener noreferrer' : undefined}
                className="rp-card-cta"
              >
                <span>{program2Cta.label || 'Learn More'}</span>
                <ArrowIcon />
              </a>
            </EditableSlotGroup>
          </div>
        </article>

        {/* Program 3 — Community Ambassador Program */}
        <article ref={row3Ref} className="reveal-fade-up rp-card">
          <EditableSlotGroup
            groupPath="rewardsprograms.program3_image"
            definition={program3ImageDef}
            className="rp-card-media"
          >
            <div className="rp-card-image-wrap">
              {program3Image.image ? (
                <VibesitesImage
                  url={program3Image.image}
                  alt={program3Image.alt || ''}
                  sizes="(min-width: 1024px) 33vw, (min-width: 640px) 50vw, 100vw"
                  className="rp-card-image"
                />
              ) : (
                <div className="rp-card-image rp-card-image--placeholder" />
              )}
            </div>
          </EditableSlotGroup>
          <div className="rp-card-body">
            <RichTextContent
              path="rewardsprograms.program3_eyebrow"
              label="Program 3 Eyebrow"
              defaultValue="<p>Community Ambassador Program</p>"
              as="p"
              className="rp-card-eyebrow"
            />
            <RichTextContent
              path="rewardsprograms.program3_title"
              label="Program 3 Title"
              defaultValue="<p>Turn Your Story Into Extra Cash.</p>"
              as="h3"
              className="rp-card-title"
            />
            <RichTextContent
              path="rewardsprograms.program3_body"
              label="Program 3 Body"
              defaultValue="<p>Love where you live? Get paid to share it. Our community ambassadors earn compensation for speaking with prospective residents about day-to-day life here.</p>"
              as="div"
              className="rp-card-text"
            />
            <EditableSlotGroup
              groupPath="rewardsprograms.program3_cta"
              definition={program3CtaDef}
            >
              <a
                href={resolveHref(program3Cta.url || '#')}
                target={program3Cta._target || '_self'}
                rel={program3Cta._target === '_blank' ? 'noopener noreferrer' : undefined}
                className="rp-card-cta"
              >
                <span>{program3Cta.label || 'Learn More'}</span>
                <ArrowIcon />
              </a>
            </EditableSlotGroup>
          </div>
        </article>
        </div>

        <RichTextContent
          path="rewardsprograms.disclaimer"
          label="Disclaimer"
          defaultValue="<p>Rewards and promotions may vary by community and over time. Terms, eligibility, and payouts are set at the community level and are subject to change without notice.</p>"
          as="div"
          className="rp-disclaimer"
        />
      </div>

      <style>{`
        .rp-section {
          position: relative;
          background: var(--vibe-color-background, #ffffff);
          /* HeroSolidBG above owns a chevron + bottom accent; WhiteStripe
             below owns its own rhythm. Keep edges tight. */
          padding: 1.75rem 1.25rem 2.25rem;
        }
        @media (min-width: 768px) {
          .rp-section { padding: 2.75rem 1.5rem 3rem; }
        }

        .rp-container {
          max-width: 1200px;
          margin: 0 auto;
        }

        /* Intro ------------------------------------------------------------ */
        .rp-intro {
          max-width: 820px;
          margin: 0 auto 2.75rem;
          text-align: center;
        }
        @media (min-width: 768px) {
          .rp-intro { margin-bottom: 3.5rem; }
        }

        .rp-intro-eyebrow,
        .rp-intro-eyebrow * {
          font-family: var(--vibe-font-heading, 'Oswald', 'Arial Narrow', system-ui, sans-serif);
          font-weight: 400;
        }
        .rp-intro-eyebrow {
          margin: 0 0 0.75rem;
          font-size: 0.8125rem;
          letter-spacing: 0.22em;
          text-transform: uppercase;
          color: var(--vibe-color-secondary, #3E441D);
        }

        .rp-intro-title,
        .rp-intro-title * {
          font-family: var(--vibe-font-heading, 'Oswald', 'Arial Narrow', system-ui, sans-serif);
          font-weight: 400;
        }
        .rp-intro-title {
          margin: 0 0 1.1rem;
          font-size: 1.75rem;
          line-height: 1.15;
          text-transform: uppercase;
          letter-spacing: 0.02em;
          color: var(--vibe-color-secondary, #3E441D);
        }
        @media (min-width: 768px) {
          .rp-intro-title { font-size: 2.25rem; }
        }
        @media (min-width: 1024px) {
          .rp-intro-title { font-size: 2.5rem; }
        }

        .rp-intro-body,
        .rp-intro-body * {
          font-family: var(--vibe-font-body);
          font-weight: 400;
        }
        .rp-intro-body {
          font-size: 0.9375rem;
          line-height: 1.6;
          color: var(--vibe-color-text, #342E1F);
        }
        .rp-intro-body p { margin: 0 0 0.6rem; }
        .rp-intro-body p:last-child { margin-bottom: 0; }

        /* Program grid: 3-up on desktop, 2-up on tablet, stacked on mobile. */
        .rp-grid {
          display: grid;
          grid-template-columns: 1fr;
          gap: 1.75rem;
        }
        @media (min-width: 640px) {
          .rp-grid {
            grid-template-columns: 1fr 1fr;
            gap: 2rem;
          }
        }
        @media (min-width: 1024px) {
          .rp-grid {
            grid-template-columns: repeat(3, 1fr);
            gap: 2.25rem;
          }
        }

        /* Card: image on top, body below. Matches the FlexibleLivingPrograms
           card treatment (white body, subtle shadow, secondary accent stripe
           reveals on hover). */
        .rp-card {
          display: flex;
          flex-direction: column;
          background: #ffffff;
          border: 1px solid rgba(0, 0, 0, 0.06);
          box-shadow: 0 10px 28px rgba(0, 0, 0, 0.08);
          overflow: hidden;
          position: relative;
          transition: transform 0.3s ease, box-shadow 0.3s ease;
        }
        .rp-card::after {
          content: '';
          position: absolute;
          left: 0;
          right: 0;
          bottom: 0;
          height: 3px;
          background: var(--cta-bg, var(--vibe-color-secondary, #3E441D));
          transform: scaleX(0);
          transform-origin: left center;
          transition: transform 0.4s cubic-bezier(0.22, 1, 0.36, 1);
        }
        .rp-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 18px 40px rgba(0, 0, 0, 0.12);
        }
        .rp-card:hover::after {
          transform: scaleX(1);
        }

        .rp-card-image-wrap {
          position: relative;
          aspect-ratio: 5 / 3;
          background: #ffffff;
          overflow: hidden;
        }
        .rp-card-image {
          width: 100%;
          height: 100%;
          object-fit: cover;
          object-position: center;
          display: block;
          transition: transform 0.5s cubic-bezier(0.22, 1, 0.36, 1);
        }
        .rp-card:hover .rp-card-image {
          transform: scale(1.04);
        }
        .rp-card-image--placeholder {
          width: 100%;
          height: 100%;
          background: linear-gradient(
            135deg,
            var(--vibe-color-surface, #f3f1ea) 0%,
            color-mix(in srgb, var(--vibe-color-primary) 10%, #ffffff) 100%
          );
        }

        .rp-card-body {
          display: flex;
          flex-direction: column;
          gap: 0.75rem;
          padding: 1.5rem 1.5rem 1.75rem;
          flex: 1;
        }

        .rp-card-eyebrow,
        .rp-card-eyebrow * {
          font-family: var(--vibe-font-heading, 'Oswald', 'Arial Narrow', system-ui, sans-serif);
          font-weight: 400;
        }
        .rp-card-eyebrow {
          margin: 0;
          font-size: 0.6875rem;
          letter-spacing: 0.22em;
          text-transform: uppercase;
          color: var(--vibe-color-secondary, #3E441D);
        }

        .rp-card-title,
        .rp-card-title * {
          font-family: var(--vibe-font-heading, 'Oswald', 'Arial Narrow', system-ui, sans-serif);
          font-weight: 400;
        }
        .rp-card-title {
          margin: 0;
          font-size: 1.25rem;
          line-height: 1.2;
          text-transform: uppercase;
          letter-spacing: 0.02em;
          color: var(--vibe-color-secondary, #3E441D);
        }
        @media (min-width: 1024px) {
          .rp-card-title { font-size: 1.375rem; }
        }

        .rp-card-text,
        .rp-card-text * {
          font-family: var(--vibe-font-body);
          font-weight: 400;
        }
        .rp-card-text {
          font-size: 0.9375rem;
          line-height: 1.55;
          color: var(--vibe-color-text, #342E1F);
          flex: 1;
        }
        .rp-card-text p { margin: 0 0 0.6rem; }
        .rp-card-text p:last-child { margin-bottom: 0; }

        .rp-card-cta {
          align-self: flex-start;
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          margin-top: 0.4rem;
          font-family: var(--vibe-font-heading, 'Oswald', 'Arial Narrow', system-ui, sans-serif);
          font-size: 0.8125rem;
          text-transform: uppercase;
          letter-spacing: 0.1em;
          color: var(--vibe-color-secondary, #3E441D);
          text-decoration: none;
          border-bottom: 1px solid currentColor;
          padding: 0.25rem 0;
          transition: color 0.25s ease, gap 0.25s ease;
        }
        .rp-card-cta:hover {
          color: var(--vibe-color-primary);
          gap: 0.75rem;
        }
        .rp-card-cta svg { transition: transform 0.25s ease; }
        .rp-card-cta:hover svg { transform: translateX(2px); }

        /* Disclaimer ------------------------------------------------------- */
        .rp-disclaimer,
        .rp-disclaimer * {
          font-family: var(--vibe-font-body);
          font-weight: 400;
        }
        .rp-disclaimer {
          margin: 2.5rem auto 0;
          max-width: 820px;
          font-size: 0.8125rem;
          line-height: 1.55;
          color: color-mix(in srgb, var(--vibe-color-text) 70%, transparent);
          text-align: center;
        }
        .rp-disclaimer p { margin: 0; }
      `}</style>
    </section>
  );
}

// ============================================================================
// Content schema
// ============================================================================

export const contentSchema: ComponentContentSchema = {
  slot: 'RewardsPrograms',
  displayName: 'Rewards Programs',
  tokens: {
    'rewardsprograms.intro_eyebrow': {
      label: 'Intro Eyebrow',
      type: 'richtext',
      defaultValue: '<p>Live Here &amp; Get Rewarded</p>',
    },
    'rewardsprograms.intro_title': {
      label: 'Intro Title',
      type: 'richtext',
      defaultValue: '<p>Living here comes with real opportunities to earn.</p>',
    },
    'rewardsprograms.intro_body': {
      label: 'Intro Body',
      type: 'richtext',
      defaultValue:
        '<p>At <span data-type="field-token" data-field="property_name"></span>, our rewards programs are built to offset rent, reduce move-in costs, and create a stronger, more connected community. Explore the three ways our residents earn below.</p>',
    },
    'rewardsprograms.program1_eyebrow': {
      label: 'Program 1 Eyebrow',
      type: 'richtext',
      defaultValue: '<p>Resident Referral Program</p>',
    },
    'rewardsprograms.program1_title': {
      label: 'Program 1 Title',
      type: 'richtext',
      defaultValue: '<p>Love Your Home? It Pays to Share It.</p>',
    },
    'rewardsprograms.program1_body': {
      label: 'Program 1 Body',
      type: 'richtext',
      defaultValue:
        '<p>Earn rent credits when you refer a friend who signs a lease. Share the home you love — and let your referrals help pay for yours.</p>',
    },
    'rewardsprograms.program2_eyebrow': {
      label: 'Program 2 Eyebrow',
      type: 'richtext',
      defaultValue: '<p>Preferred Employer Program</p>',
    },
    'rewardsprograms.program2_title': {
      label: 'Program 2 Title',
      type: 'richtext',
      defaultValue: '<p>Your Job Comes with Built-In Savings.</p>',
    },
    'rewardsprograms.program2_body': {
      label: 'Program 2 Body',
      type: 'richtext',
      defaultValue:
        '<p>Eligible employees from partner companies receive monthly rent reductions on top of any current leasing specials. Ask our team to see if your employer qualifies.</p>',
    },
    'rewardsprograms.program3_eyebrow': {
      label: 'Program 3 Eyebrow',
      type: 'richtext',
      defaultValue: '<p>Community Ambassador Program</p>',
    },
    'rewardsprograms.program3_title': {
      label: 'Program 3 Title',
      type: 'richtext',
      defaultValue: '<p>Turn Your Story Into Extra Cash.</p>',
    },
    'rewardsprograms.program3_body': {
      label: 'Program 3 Body',
      type: 'richtext',
      defaultValue:
        '<p>Love where you live? Get paid to share it. Our community ambassadors earn compensation for speaking with prospective residents about day-to-day life here.</p>',
    },
    'rewardsprograms.disclaimer': {
      label: 'Disclaimer',
      type: 'richtext',
      defaultValue:
        '<p>Rewards and promotions may vary by community and over time. Terms, eligibility, and payouts are set at the community level and are subject to change without notice.</p>',
    },
  },
  slotGroups: {
    'rewardsprograms.program1_image': program1ImageDef,
    'rewardsprograms.program1_cta': program1CtaDef,
    'rewardsprograms.program2_image': program2ImageDef,
    'rewardsprograms.program2_cta': program2CtaDef,
    'rewardsprograms.program3_image': program3ImageDef,
    'rewardsprograms.program3_cta': program3CtaDef,
  },
};

(RewardsPrograms as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default RewardsPrograms;
